/**
 * @module features/users/components/UserFilters
 * @description Search and role filter toolbar modeled after Reference Image 1 & 2.
 * Features rounded-2xl container, Inter font, ⌘K search indicator, and segmented pill buttons.
 */

import * as React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface UserFiltersProps {
  search: string;
  role: string;
  onSearchChange: (value: string) => void;
  onRoleChange: (role: string) => void;
}

export function UserFilters({
  search,
  role,
  onSearchChange,
  onRoleChange,
}: UserFiltersProps) {
  const [localSearch, setLocalSearch] = React.useState(search);

  // Debounce search update
  React.useEffect(() => {
    if (localSearch === search) return;
    const handler = setTimeout(() => {
      onSearchChange(localSearch);
    }, 250);

    return () => clearTimeout(handler);
  }, [localSearch, search, onSearchChange]);

  // Sync external search changes
  React.useEffect(() => {
    setLocalSearch((prev) => (prev !== search ? search : prev));
  }, [search]);

  const roles = [
    { label: "All Users", value: "all" },
    { label: "Super Admins", value: "super_admin" },
    { label: "Clients", value: "client" },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card dark:bg-zinc-950 p-3 sm:p-3.5 rounded-2xl border border-border/70 dark:border-zinc-800/80 shadow-xs">
      {/* Search Input with ⌘K Badge */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground shrink-0 pointer-events-none" />
        <Input
          type="text"
          placeholder="Search users by name, email, or role..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-10 pr-12 h-10 text-sm bg-muted/40 dark:bg-zinc-900 border-border/70 dark:border-zinc-800 rounded-xl focus-visible:ring-1 focus-visible:ring-ring"
        />
        {localSearch ? (
          <button
            type="button"
            onClick={() => {
              setLocalSearch("");
              onSearchChange("");
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center pointer-events-none">
            <span className="text-sm text-foreground">⌘K</span>
          </div>
        )}
      </div>

      {/* Segmented Filter Pills from Reference 1 */}
      <div className="flex items-center gap-1 p-1 bg-muted/60 dark:bg-zinc-900 rounded-xl border border-border/40 dark:border-zinc-800/80 overflow-x-auto">
        {roles.map((r) => {
          const isActive = role === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => onRoleChange(r.value)}
              className={`flex-1 sm:flex-none px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 whitespace-nowrap ${isActive
                ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
